import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { cn, QueryableComponent } from '@prenuvo/halo';

import { CharacterLimitIndicator } from '../character-limit-indicator.component';
import { BaseQuillEditor, BaseQuillEditorRef } from './base-quill-editor.component';
import { useQuillEditor } from './use-quill-editor.hook';

export interface FindingEditorProps
  extends QueryableComponent, Omit<ComponentPropsWithoutRef<'div'>, 'onBlur' | 'onChange' | 'ref'> {
  displayAsBullets?: boolean;
  isReadOnly?: boolean;
  maxLength?: number;
  onBlur?: (editedText: string, currentText?: string) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
}

export const FindingEditor = forwardRef<BaseQuillEditorRef, FindingEditorProps>(
  (
    {
      className,
      'data-testid': dataTestId,
      displayAsBullets = false,
      isReadOnly = false,
      maxLength,
      onBlur,
      onChange,
      placeholder = '',
      value,
      ...props
    },
    ref,
  ) => {
    const { blur, characterCount, editorRef, isFocused } = useQuillEditor({
      displayAsBullets,
      isReadOnly,
      maxLength,
      onBlur,
      onChange,
      placeholder,
      value,
    });

    return (
      <div className={cn('finding-editor flex flex-col', className)}>
        <BaseQuillEditor
          {...props}
          blur={blur}
          data-testid={dataTestId ?? 'finding-editor'}
          editorRef={editorRef}
          ref={ref}
        />
        {typeof maxLength === 'number' ? (
          <CharacterLimitIndicator
            currentLength={characterCount}
            isActive={isFocused}
            maxLength={maxLength}
          />
        ) : null}
      </div>
    );
  },
);

FindingEditor.displayName = 'FindingEditor';
